import React, { ComponentType, useEffect } from 'react';

import { shallowEqual, useSelector } from 'react-redux';

import { useHistory__UNSAFE } from 'src/router/hooks';
import { RouteComponentProps } from 'src/router/types';
import { ApdexTask } from 'src/types/apdex';
import { BucketState } from 'src/types/state';
import { stopApdex, startApdex } from 'src/utils/analytics/apdex';

const displayName = (c: ComponentType) =>
  c.displayName || c.name || 'Component';

export type ApdexOptions = {
  featureFlagSelectors?: Array<{
    name: string;
    selector: (initialState: BucketState) => boolean;
  }>;
  stopTimePerformanceMarkEntryName?: string;
};

type FeatureFlagMap = { [flagName: string]: boolean };

// Hook that measures Apdex once the consuming component has finished its initial render
export const useApdexMeasuredRender = (
  eventName: ApdexTask | undefined,
  options: ApdexOptions = {}
) => {
  const {
    featureFlagSelectors = [],
    stopTimePerformanceMarkEntryName,
  } = options;

  // Using `shallowEqual` here is *very* important, otherwise this will force
  // the consuming component (usually page-level) to re-render on every change
  // to the redux store
  const featureFlagMap = useSelector<BucketState, FeatureFlagMap | null>(
    state => {
      if (featureFlagSelectors.length === 0) {
        return null;
      }

      return featureFlagSelectors.reduce(
        (acc, { name, selector }) => ({
          ...acc,
          ...{ [name]: selector(state) },
        }),
        {}
      );
    },
    shallowEqual
  );

  const additionalAttributes = featureFlagMap
    ? { featureFlags: featureFlagMap }
    : undefined;

  useEffect(() => {
    if (eventName) {
      stopApdex(eventName, {
        additionalAttributes,
        stopTimePerformanceMarkEntryName,
      });
    }
    // `stopApdex` is only meant to be called on the initial mount of the component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const withApdex = (
  eventName: ApdexTask | undefined,
  options: ApdexOptions = {}
) => <P extends object>(Comp: ComponentType<P>) => {
  function WithApdex(props: P) {
    useApdexMeasuredRender(eventName, options);
    return <Comp {...props} />;
  }
  WithApdex.displayName = `withApdex(${displayName(Comp)})`;
  WithApdex.WrappedComponent = Comp;
  return WithApdex;
};

export const useApdexMeasuredBrowserNavigation = (
  eventName: ApdexTask,
  history: RouteComponentProps['history']
) => {
  useEffect(() => {
    return history.listen((_, action) => {
      if (action === 'POP') {
        startApdex({
          task: eventName,
          type: 'transition',
        });
      }
    });
  }, [eventName, history]);
};

/**
 * Higher order component that fires an Apdex transition event when the user uses
 * browsers navigation button (back/forward).
 *
 * Only route components should be wrapped into this HOC.
 */

export const withApdexTransition = (eventName: ApdexTask) => <
  P extends RouteComponentProps
>(
  Comp: ComponentType<P>
) => {
  function WithApdexTransition(props: P) {
    const history = useHistory__UNSAFE();
    useApdexMeasuredBrowserNavigation(eventName, props.history || history);
    return <Comp {...props} />;
  }
  WithApdexTransition.displayName = `WithApdexTransition(${displayName(Comp)})`;
  WithApdexTransition.WrappedComponent = Comp;
  return WithApdexTransition;
};
